import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";

import { ConfirmDialog, Dialog, DialogFooter, DialogHeader, Dropdown, FormField } from "@/ds";
import { CampoDecimal } from "@/shared/components";
import {
  ANO_REFERENCIA_LABEL,
  CUSTO_PLACEHOLDER,
  EQUIPAMENTO_LABEL,
  FABRICANTE_LABEL,
  HORAS_LABEL,
  HORAS_PLACEHOLDER,
  MATERIAIS_LABEL,
  MODELO_LABEL,
  MOEDA_LABEL,
  MOEDA_MATERIAIS_QUALIFICADOR,
  MOEDA_SERVICOS_QUALIFICADOR,
  PREENCHIDO_PELA_TAG_PLACEHOLDER,
  SELECIONE_PLACEHOLDER,
  SERVICOS_LABEL,
  TIPO_OBRA_LABEL,
  EMBARCACAO_LABEL,
  GRUPO_LABEL,
} from "./escopo-padrao-dialog.constants";
import styles from "./escopo-padrao-dialog.module.scss";
import type {
  CampoSelecao,
  CampoValorMoeda,
  EscopoPadraoDialogViewModel,
} from "./types-escopo-padrao-dialog";

const RODAPE_DIALOGO = { footer: { className: styles.dialogoRodape } };
const PAINEL = { panel: { className: styles.painel } };

function Seletor({
  rotulo,
  campo,
  placeholder = SELECIONE_PLACEHOLDER,
  desabilitado,
  filtrar = false,
  qualificador,
}: {
  rotulo: string;
  campo: CampoSelecao;
  placeholder?: string;
  desabilitado?: boolean;
  filtrar?: boolean;
  qualificador?: string;
}) {
  return (
    <FormField.Root required={campo.obrigatorio ?? true} error={campo.erro}>
      <FormField.Label>
        {rotulo}
        {qualificador ? <span className={styles.srOnly}> {qualificador}</span> : null}
      </FormField.Label>
      <FormField.Control>
        <Dropdown
          appendTo="self"
          pt={PAINEL}
          value={campo.valor}
          options={campo.opcoes}
          optionDisabled="disabled"
          placeholder={placeholder}
          filter={filtrar}
          showClear={false}
          disabled={desabilitado}
          onChange={(evento) => campo.escolher(evento.value ?? null)}
          data-testid={campo.testId}
        />
      </FormField.Control>
      <FormField.Error />
    </FormField.Root>
  );
}

function SomenteLeitura({
  rotulo,
  valor,
  testId,
  placeholder,
}: {
  rotulo: string;
  valor: string;
  testId: string;
  placeholder?: string;
}) {
  return (
    <FormField.Root disabled>
      <FormField.Label>{rotulo}</FormField.Label>
      <FormField.Control>
        <InputText value={valor} placeholder={placeholder} readOnly disabled data-testid={testId} />
      </FormField.Control>
    </FormField.Root>
  );
}

function ValorEMoeda({
  rotulo,
  qualificadorMoeda,
  campo,
  desabilitado,
}: {
  rotulo: string;
  qualificadorMoeda: string;
  campo: CampoValorMoeda;
  desabilitado: boolean;
}) {
  return (
    <div className={styles.valorMoeda}>
      <CampoDecimal
        label={rotulo}
        casas={2}
        placeholder={CUSTO_PLACEHOLDER}
        value={campo.valor.valor}
        onChange={campo.valor.digitar}
        error={campo.valor.erro}
        disabled={desabilitado}
        testId={campo.valor.testId}
      />
      <Seletor
        rotulo={MOEDA_LABEL}
        qualificador={qualificadorMoeda}
        campo={campo.moeda}
        desabilitado={desabilitado}
        filtrar
      />
    </div>
  );
}

export function EscopoPadraoDialogView({
  formId,
  titulo,
  salvarLabel,
  anoReferencia,
  aviso,
  bloqueados,
  bloqueadosApos,
  secoes,
  apoio,
  tipo,
  embarcacao,
  grupo,
  equipamento,
  fabricante,
  modelo,
  escopo,
  custos,
  isSubmitting,
  submitError,
  enviar,
  cancelar,
  descarte,
}: EscopoPadraoDialogViewModel) {
  return (
    <>
      <Dialog
        visible
        size="lg"
        className={styles.dialogo}
        contentClassName={styles.corpo}
        onHide={cancelar}
        locked={isSubmitting}
        focusOnShow={false}
        header={<DialogHeader title={titulo} />}
        pt={RODAPE_DIALOGO}
        footer={
          <DialogFooter
            className={styles.rodape}
            confirmLabel={salvarLabel}
            formId={formId}
            loading={isSubmitting}
            locked={isSubmitting}
            onCancel={cancelar}
            testId="escopo-padrao"
          />
        }
        data-testid="escopo-padrao-dialog"
      >
        <form id={formId} className={styles.form} onSubmit={enviar} noValidate>
          <h3 className={styles.secaoTitulo}>{secoes.identificacao}</h3>
          <div className={styles.identificacao}>
            {bloqueados.map((campo) => (
              <SomenteLeitura
                key={campo.rotulo}
                rotulo={campo.rotulo}
                valor={campo.valor}
                testId={campo.testId}
              />
            ))}
            {tipo ? <Seletor rotulo={TIPO_OBRA_LABEL} campo={tipo} /> : null}
            {grupo ? <Seletor rotulo={GRUPO_LABEL} campo={grupo} /> : null}
            <SomenteLeitura
              rotulo={ANO_REFERENCIA_LABEL}
              valor={anoReferencia}
              testId="escopo-padrao-ano-referencia"
            />
            {bloqueadosApos.map((campo) => (
              <SomenteLeitura
                key={campo.rotulo}
                rotulo={campo.rotulo}
                valor={campo.valor}
                testId={campo.testId}
              />
            ))}
            {embarcacao ? <Seletor rotulo={EMBARCACAO_LABEL} campo={embarcacao} filtrar /> : null}
            {equipamento ? (
              <Seletor
                rotulo={EQUIPAMENTO_LABEL}
                campo={equipamento}
                desabilitado={equipamento.aguardando || equipamento.carregando}
                filtrar
              />
            ) : null}
            {fabricante !== null ? (
              <SomenteLeitura
                rotulo={FABRICANTE_LABEL}
                valor={fabricante}
                placeholder={PREENCHIDO_PELA_TAG_PLACEHOLDER}
                testId="escopo-padrao-fabricante"
              />
            ) : null}
            {modelo !== null ? (
              <SomenteLeitura
                rotulo={MODELO_LABEL}
                valor={modelo}
                placeholder={PREENCHIDO_PELA_TAG_PLACEHOLDER}
                testId="escopo-padrao-modelo"
              />
            ) : null}
            {escopo ? (
              <FormField.Root required error={escopo.erro}>
                <FormField.Label>{escopo.rotulo}</FormField.Label>
                <FormField.Control>
                  <InputText
                    value={escopo.valor}
                    maxLength={escopo.max}
                    placeholder={escopo.placeholder}
                    disabled={isSubmitting}
                    onChange={(evento) => escopo.digitar(evento.target.value)}
                    data-testid={escopo.testId}
                  />
                </FormField.Control>
                <FormField.Error />
              </FormField.Root>
            ) : null}
          </div>

          {secoes.custo && custos.length > 0 ? (
            <h3 className={styles.secaoTitulo}>{secoes.custo}</h3>
          ) : null}
          {custos.map((secao) => (
            <fieldset key={secao.periodicidade} className={styles.custo} data-testid={secao.testId}>
              {secao.titulo ? <legend className={styles.custoTitulo}>{secao.titulo}</legend> : null}
              {secao.horas ? (
                <CampoDecimal
                  label={HORAS_LABEL}
                  casas={0}
                  placeholder={HORAS_PLACEHOLDER}
                  value={secao.horas.valor}
                  onChange={secao.horas.digitar}
                  error={secao.horas.erro}
                  disabled={isSubmitting}
                  testId={secao.horas.testId}
                />
              ) : null}
              <ValorEMoeda
                rotulo={SERVICOS_LABEL}
                qualificadorMoeda={MOEDA_SERVICOS_QUALIFICADOR}
                campo={secao.servicos}
                desabilitado={isSubmitting}
              />
              <ValorEMoeda
                rotulo={MATERIAIS_LABEL}
                qualificadorMoeda={MOEDA_MATERIAIS_QUALIFICADOR}
                campo={secao.materiais}
                desabilitado={isSubmitting}
              />
            </fieldset>
          ))}

          {apoio ? (
            <p className={styles.apoio} data-testid="escopo-padrao-apoio">
              {apoio}
            </p>
          ) : null}

          {aviso ? (
            <Message
              className="p-message-block"
              severity="info"
              text={aviso}
              data-testid="escopo-padrao-aviso"
            />
          ) : null}

          {submitError ? (
            <Message
              className="p-message-block"
              severity="error"
              text={submitError}
              data-testid="escopo-padrao-submit-error"
            />
          ) : null}
        </form>
      </Dialog>
      {descarte ? <ConfirmDialog {...descarte} /> : null}
    </>
  );
}
